import { Box, Card, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";

import type { FC, ReactNode } from "react";

interface RecentlyEditedRowProps {
  /** What fills the square on the left: a channel icon, a thumbnail. */
  media: ReactNode;
  title: string;
  href: string;
  /** The line under the title, of `RowDetail`s. */
  details: ReactNode;
  pill?: ReactNode;
  analyticsId: string;
}

/** One entry in the Recently edited feed: its media, its title as a link, and what it is. */
export const RecentlyEditedRow: FC<RecentlyEditedRowProps> = ({
  media,
  title,
  href,
  details,
  pill,
  analyticsId,
}) => (
  <Card component="li" elevation={5} sx={{ display: "flex", alignItems: "stretch", minHeight: 88 }}>
    <Box
      sx={{
        flexShrink: 0,
        width: 88,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRight: 1,
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {media}
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 0, px: 2, py: 1.5 }}>
      <Link
        component={RouterLink}
        to={href}
        variant="subtitle1"
        color="textPrimary"
        underline="hover"
        data-analytics-id={analyticsId}
        sx={{
          display: "block",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </Link>
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.5, mt: 0.5 }}>
        {details}
      </Box>
    </Box>
    {pill && <Box sx={{ display: "flex", alignItems: "center", pr: 2 }}>{pill}</Box>}
  </Card>
);

/** One fact under a row's title, with its icon. */
export const RowDetail: FC<{ icon?: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <Typography
    component="span"
    variant="body2"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      color: "text.secondary",
      "& > .MuiSvgIcon-root": { fontSize: 18 },
    }}
  >
    {icon}
    {children}
  </Typography>
);
